/* iter309 — Compliance Center widget for the admin dashboard.
 *
 * Single surface that summarises the last 30 days of:
 *   • Patient-safety incidents (MDR Rule 11 leaks) — iter308
 *   • Macro validation corrections (frontend regressions) — iter307
 *   • GDPR consent coverage + DOB uptake — iter305 / iter309
 *   • AI decisions volume per feature — iter306
 *
 * Data source: GET /api/admin/compliance/overview
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { ShieldCheck, AlertTriangle, Sparkles, Users, Loader2, RefreshCw } from "lucide-react";

function Section({ title, icon: Icon, children, testid }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 bg-white/60 dark:bg-black/20" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-sky-600" />
        <h4 className="text-[12px] font-semibold text-gray-900 dark:text-gray-100">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, tone = "default", testid }) {
  const tones = {
    default: "text-gray-900 dark:text-gray-100",
    ok: "text-emerald-700 dark:text-emerald-300",
    warn: "text-amber-700 dark:text-amber-300",
    bad: "text-rose-700 dark:text-rose-300",
  };
  return (
    <div className="flex items-baseline justify-between gap-2 text-[11px]" data-testid={testid}>
      <span className="text-gray-600 dark:text-gray-400">{label}</span>
      <span className={`font-bold ${tones[tone]}`}>{value}</span>
    </div>
  );
}

export default function ComplianceCenterWidget() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const bg = language === "bg";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/admin/compliance/overview`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 rounded-xl border border-gray-200 dark:border-gray-700" data-testid="compliance-center-loading">
        <Loader2 size={14} className="animate-spin" />
        <span className="text-[11px] text-gray-500">{bg ? "Зареждам..." : "Loading..."}</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-3 rounded-xl border border-rose-200 bg-rose-50 dark:bg-rose-950/30 text-[11px] text-rose-800 dark:text-rose-200" data-testid="compliance-center-error">
        {bg ? "Грешка при зареждане: " : "Error loading: "}{error}
      </div>
    );
  }
  if (!data) return null;

  const ps = data.patient_safety || {};
  const mv = data.macro_validation || {};
  const gd = data.gdpr || {};
  const ai = data.ai_decisions || {};
  const incidentTone = ps.incidents_total > 20 ? "bad" : ps.incidents_total > 0 ? "warn" : "ok";
  const consentCoverage = gd.consent_coverage_pct || 0;
  const coverageTone = consentCoverage >= 95 ? "ok" : consentCoverage >= 70 ? "warn" : "bad";

  return (
    <div className="space-y-3" data-testid="compliance-center-widget">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-emerald-600" />
          <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">
            {bg ? "Compliance Center — последни 30 дни" : "Compliance Center — last 30 days"}
          </h3>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
          data-testid="compliance-center-refresh"
          title={bg ? "Обнови" : "Refresh"}
        >
          <RefreshCw size={11} /> {bg ? "Обнови" : "Refresh"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Section
          title={bg ? "Patient-safety incidents (MDR Rule 11)" : "Patient-safety incidents (MDR Rule 11)"}
          icon={AlertTriangle}
          testid="compliance-patient-safety"
        >
          <Stat
            label={bg ? "Общо инциденти (30д)" : "Total incidents (30d)"}
            value={ps.incidents_total ?? 0}
            tone={incidentTone}
            testid="compliance-incidents-total"
          />
          {Array.isArray(ps.top_leaked_terms) && ps.top_leaked_terms.length > 0 ? (
            <div className="mt-2 space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">
                {bg ? "Най-често лек-ани термини" : "Most-leaked terms"}
              </p>
              {ps.top_leaked_terms.slice(0, 5).map((t) => (
                <div key={t.term} className="flex items-center justify-between text-[11px]" data-testid={`compliance-leaked-${t.term}`}>
                  <span className="text-gray-700 dark:text-gray-300">{t.term}</span>
                  <span className="font-bold text-amber-700 dark:text-amber-400">{t.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-[10px] italic text-emerald-700 dark:text-emerald-300">
              {bg ? "Никакви леакове — филтърът работи." : "No leaks — filter is working."}
            </p>
          )}
        </Section>

        <Section
          title={bg ? "Macro validation (frontend регресии)" : "Macro validation (frontend regressions)"}
          icon={Sparkles}
          testid="compliance-macro-validation"
        >
          <Stat label={bg ? "Проверени meals" : "Checked meals"} value={mv.total ?? 0} testid="compliance-macro-total" />
          {mv.by_status && (
            <div className="mt-1 grid grid-cols-3 gap-1 text-center">
              <div className="rounded bg-emerald-50 dark:bg-emerald-950/40 py-1">
                <div className="text-[10px] text-emerald-700 dark:text-emerald-300">OK</div>
                <div className="text-xs font-bold text-emerald-800 dark:text-emerald-200">{mv.by_status.ok || 0}</div>
              </div>
              <div className="rounded bg-sky-50 dark:bg-sky-950/40 py-1">
                <div className="text-[10px] text-sky-700 dark:text-sky-300">{bg ? "Корегирани" : "Corrected"}</div>
                <div className="text-xs font-bold text-sky-800 dark:text-sky-200">{mv.by_status.corrected || 0}</div>
              </div>
              <div className="rounded bg-amber-50 dark:bg-amber-950/40 py-1">
                <div className="text-[10px] text-amber-700 dark:text-amber-300">{bg ? "Маркирани" : "Flagged"}</div>
                <div className="text-xs font-bold text-amber-800 dark:text-amber-200">{mv.by_status.flagged || 0}</div>
              </div>
            </div>
          )}
          {Array.isArray(mv.top_foods) && mv.top_foods.length > 0 && (
            <div className="mt-2 space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">
                {bg ? "Най-често проблемни храни" : "Most-problematic foods"}
              </p>
              {mv.top_foods.slice(0, 5).map((f, i) => (
                <div key={i} className="flex items-center justify-between text-[11px]">
                  <span className="truncate text-gray-700 dark:text-gray-300">{f.food_name}</span>
                  <span className="font-bold text-amber-700 dark:text-amber-400">
                    {f.count}× ({Math.round(f.worst_deviation_pct || 0)}%)
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section
          title={bg ? "GDPR съгласия (Чл. 7 + 8 + 9)" : "GDPR consents (Art. 7 + 8 + 9)"}
          icon={Users}
          testid="compliance-gdpr"
        >
          <Stat label={bg ? "Общо потребители" : "Total users"} value={gd.total_users ?? 0} testid="compliance-gdpr-total-users" />
          <Stat
            label={bg ? "Със записано съгласие" : "With consent record"}
            value={`${gd.users_with_consents ?? 0} (${consentCoverage}%)`}
            tone={coverageTone}
            testid="compliance-gdpr-coverage"
          />
          <Stat
            label={bg ? "Предоставили DOB (Art. 8)" : "Provided DOB (Art. 8)"}
            value={gd.users_with_dob ?? 0}
            testid="compliance-gdpr-dob"
          />
          <Stat label={bg ? "Нови signup-и (30д)" : "New signups (30d)"} value={gd.new_signups_30d ?? 0} testid="compliance-gdpr-signups" />
        </Section>

        <Section
          title={bg ? "AI decisions (EU AI Act Чл. 12)" : "AI decisions (EU AI Act Art. 12)"}
          icon={Sparkles}
          testid="compliance-ai"
        >
          <Stat label={bg ? "Общо LLM повиквания" : "Total LLM calls"} value={ai.total ?? 0} testid="compliance-ai-total" />
          {Array.isArray(ai.by_feature) && ai.by_feature.length > 0 && (
            <div className="mt-2 space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">
                {bg ? "По функционалност" : "By feature"}
              </p>
              {ai.by_feature.slice(0, 6).map((f) => {
                const errRate = f.count ? (f.errors / f.count) * 100 : 0;
                return (
                  <div key={f.feature_id} className="flex items-center justify-between text-[11px]">
                    <span className="text-gray-700 dark:text-gray-300">{f.feature_id}</span>
                    <span className="font-bold text-gray-800 dark:text-gray-200">
                      {f.count}{f.errors ? ` (${errRate.toFixed(0)}% err)` : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
